import moment from "moment";
import AppConstants from "../../constants";
import { EXPRESS_DELIVERY_TYPE } from "../../constants/ExpressConstants";
import {
  getUniqueValues,
  fixDecimals,
} from "../../utils/helpers.utils";
import {
  ExpressOrdersDataResponse,
  Order,
} from "../SharedTransform/interface";

import { getFilterAttributeValue, getAddressInfo, getActionItem } from "../SharedTransform/response.transforms";

export interface ReasonHis {
  reasonCode: string;
  reasonDescription: string;
  comment: string;
}
export interface OrderHistory {
  consignmentCode: string;
  countryCode: string;
  customerArea: string;
  deliveryDate: string;
  hubId: string;
  hubName: string;
  initiatedBy: string;
  orderType: string;
  reason: ReasonHis;
  status: string;
  zoneId: string;
  deliveredAt: string;
}

export interface HistoryOrdersDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: OrderHistory[];
}

export const historyOrdersDataTransform = (
  response: HistoryOrdersDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response?.elements?.forEach((order: OrderHistory) => {
    const {
      consignmentCode,
      deliveredAt,
      customerArea,
      deliveryDate,
      hubId,
      hubName,
      initiatedBy,
      orderType,
      reason,
      status,
      zoneId,
    } = order;
    const delDate = moment(deliveryDate, AppConstants.DATE_FORMAT_BACKEND);
    const commDate = moment(deliveredAt, AppConstants.DATE_FORMAT_BACKEND);
    transformedArray.push({
      hubCode: hubId,
      hubNames: hubName,
      consignmentId: consignmentCode,
      deliveryStatus: status,
      deliveryArea: customerArea ? customerArea : "",
      deliveryDate: delDate.format(AppConstants.DATE_FORMAT),
      deliveryZone: zoneId,
      reason: reason,
      initiatedBy: initiatedBy,
      deliveredAt: commDate.format(AppConstants.DATE_FORMAT),
      orderType: orderType,
      reasonDescription: reason ? reason?.reasonDescription : "",
      comment: reason ? reason?.comment : "",
    });
  });
  return [...transformedArray];
};

export const expressOrdersDataTransform = (
  response: ExpressOrdersDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response?.elements?.forEach((order: Order) => {
    const {
      id,
      metadata,
      posInformation,
      deliveryInformation,
      orderCode,
      createdAt,
      products,
      reason,
      consignmentCode,
    } = order;
    const deliveryDate = moment(
      deliveryInformation &&
        deliveryInformation.deliverySlot &&
        deliveryInformation.deliverySlot.startTime,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const customerContactInfo = (deliveryInformation &&
      deliveryInformation.customerContactInfo) || {
      address: {},
    };
    const transactionDate = moment(createdAt, AppConstants.DATE_FORMAT_BACKEND);
    //const timeBetweenSlots = calculateTimeBetweenSlots(startTime, endTime);
    transformedArray.push({
      id: id ? id : consignmentCode,
      hubCode: posInformation && posInformation.store,
      hubNames: posInformation && posInformation.storeName,
      consignmentId: consignmentCode,
      status: metadata && metadata.status,
      deliveryStatus: metadata && metadata.deliveryStatus,
      deliveryArea:
        customerContactInfo && customerContactInfo.address
          ? customerContactInfo.address.area
          : "",
      deliveryDate: deliveryDate.format(AppConstants.DATE_FORMAT),
      numberOfItems: products && products?.length ? products?.length : 0,
      deliverySlotTime: deliveryInformation?.deliverySlot?.deliverySlotTime,
      promiseTime:
        deliveryInformation &&
        deliveryInformation?.deliverySlot &&
        deliveryInformation.deliverySlot.promiseDeliveryTime
          ? deliveryInformation.deliverySlot.promiseDeliveryTime + " mins"
          : "-",
      orderId: orderCode,
      transactionDate: transactionDate?.format(AppConstants.DATE_FORMAT),
      customerName: customerContactInfo?.name,
      deliveryStreetAddress: getAddressInfo(customerContactInfo.address),
      deliveryZone:
        metadata && metadata?.deliveryZone ? metadata?.deliveryZone : "",
      driverName:
        deliveryInformation && deliveryInformation.driver
          ? deliveryInformation.driver.driverName
          : "",
      vehicle:
        deliveryInformation && deliveryInformation.preferredVehicle
          ? deliveryInformation.preferredVehicle
          : "",
      paymentType: metadata && metadata.paymentType,
      totalAmount: fixDecimals(
        2,
        metadata && metadata.totalAmount ? metadata.totalAmount.toString() : ""
      ),
      codAmount: fixDecimals(
        2,
        metadata && metadata.amountToCollect
          ? metadata.amountToCollect.toString()
          : ""
      ),
      collectedAmount:
        metadata && metadata.collectedAmount
          ? parseFloat(metadata.collectedAmount.toString()).toFixed(2)
          : "0.00",
      deliveryActualTime: deliveryInformation?.actualTransitTimeInMin,
      deliveryExecutionStatus: deliveryInformation
        ? deliveryInformation?.deliveryExecutionStatus
        : "-",
      address:
        customerContactInfo && customerContactInfo?.address
          ? getAddressInfo(customerContactInfo.address)
          : "-",
      actions: getActionItem(deliveryInformation, metadata?.deliveryStatus),
      addressStatus:
        customerContactInfo && customerContactInfo?.address
          ? customerContactInfo.address?.status
          : "",
      undeliveredReason: reason ? reason?.reasonDescription : "",
      deliveryType: metadata?.deliveryType,
    });
  });
  return [...transformedArray];
};

const getFilterAttributeStatus = (attrValue: any, defaultStatus: any) => {
  const status = defaultStatus;
  let currentValue = attrValue.map((item: any) => item.value);

  const selectAllIdx = currentValue.indexOf(AppConstants.SELECT_ALL.value);
  const selectNoneIdx = currentValue.indexOf(AppConstants.SELECT_NONE.value);
  if (selectAllIdx > -1) {
    currentValue = status;
  }
  if (selectNoneIdx > -1) {
    currentValue = status;
  }
  return currentValue;
};

export const createFilterPayload = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  countryCode?: any,
  selectedChip?: any
) => {
  payload.attributes = [];
  if (countryCode) {
    payload.countryISOCode = countryCode;
    payload.countryCode = countryCode;
  }

  if (payload.dateRange) {
    if (payload.dateRange && payload.dateRange.value) {
      payload.isDateRange = true;
      payload.dateRange = {
        startTime: moment(
          payload.dateRange.value.startDate,
          AppConstants.DATE_FORMAT
        )
          .hours(0)
          .minutes(0)
          .seconds(0)
          .format(AppConstants.DATE_FORMAT_BACKEND),
        endTime: moment(
          payload.dateRange.value.endDate,
          AppConstants.DATE_FORMAT
        )
          .hours(23)
          .minutes(59)
          .seconds(59)
          .format(AppConstants.DATE_FORMAT_BACKEND),
      };
    }
  }
  if (payload.deliveryEndTime) {
    if (payload.dateRange) {
      payload.isDateRange = true;
      const [hours, mins] = payload.deliveryEndTime.split(":");
      payload.dateRange = {
        startTime: moment(payload.dateRange.startTime),
        endTime: moment(payload.dateRange.endTime)
          .hours(hours)
          .minutes(mins)
          .seconds(59)
          .format(AppConstants.DATE_FORMAT_BACKEND),
      };
    }
  }
  if (payload.deliveryStatTime) {
    if (payload.dateRange) {
      payload.isDateRange = true;
      const [hours, mins] = payload.deliveryStatTime.split(":");
      payload.dateRange = {
        startTime: moment(payload.dateRange.startTime)
          .hours(hours)
          .minutes(mins)
          .seconds(59)
          .format(AppConstants.DATE_FORMAT_BACKEND),
        endTime: moment(payload.dateRange.endTime),
      };
    }
  }
  if (payload.hub.value.length > 0) {
    let attrValue = [];
    if (payload.hub.value) {
      attrValue = getFilterAttributeValue(payload.hub.value);
      payload.attributes.push({
        key: "hub",
        value: attrValue,
      });
    }
  } else {
    delete payload.hub;
  }
  if (payload.hubName.value.length > 0) {
    let attrValue = [];
    if (payload.hubName.value) {
      attrValue = getFilterAttributeValue(payload.hubName.value);
      payload.attributes.push({
        key: "hubName",
        value: attrValue,
      });
    }
  } else {
    delete payload.hubName;
  }
  if (payload.deliveryArea.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryArea.value) {
      attrValue = getFilterAttributeValue(payload.deliveryArea.value);
      payload.attributes.push({
        key: "customerArea",
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryArea;
  }

  if (payload.deliveryZone.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryZone.value) {
      attrValue = getFilterAttributeValue(payload.deliveryZone.value);
      payload.attributes.push({
        key: "zoneId",
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryZone;
  }
  if (payload.reason.value.length > 0) {
    let attrValue = [];
    if (payload.reason.value) {
      attrValue = getFilterAttributeValue(payload.reason.value);
      payload.attributes.push({
        key: "reason",
        value: attrValue,
      });
    }
  } else {
    delete payload.reason;
  }

  if (payload.deliveryStatus.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryStatus.value) {
      attrValue = getFilterAttributeValue(payload.deliveryStatus.value);
      payload.attributes.push({
        key: payload.deliveryStatus.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryStatus;
  }

  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }
  payload.isDateRange = true;
  delete payload.hub;
  delete payload.hubName;
  delete payload.paymentType;
  delete payload.vehicle;
  delete payload.addressStatus;
  delete payload.reset;
  delete payload.deliveryArea;
  delete payload.deliveryStatus;
  delete payload.deliveryExecutionStatus;
  delete payload.status;
  delete payload.deliverySlotTime;
  delete payload.reason;
  delete payload.driverId;
  delete payload.orderType;
  delete payload.deliveryZone;
  return payload;
};

export const createFilterPayloadSTD = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  countryCode?: any,
  isExpress?: any,
  isRoute?: boolean
) => {
  payload.attributes = [];
  const initialIsExpress = isExpress;
  isExpress = false;
  if (countryCode) {
    payload.countryCode = countryCode;
    // payload.countryCode = countryCode
  }
  if (payload.hub) {
    if (payload.hub.value && payload.hub.value.length > 0) {
      let hubValue = payload.hub.value.map((item: any) => item.value);
      let hubNameValue = [];
      if (payload.hubName && payload.hubName.value.length) {
        hubNameValue = payload.hubName.value.map((item: any) => item.value);
      }
      hubValue = hubValue.concat(hubNameValue);
      const selectAllIdx = hubValue.indexOf(AppConstants.SELECT_ALL.value);
      const selectNoneIdx = hubValue.indexOf(AppConstants.SELECT_NONE.value);
      if (selectAllIdx > -1) {
        hubValue = [];
      }
      if (selectNoneIdx > -1) {
        hubValue = [];
      }
      payload.attributes.push({
        key: payload.hub.fieldName,
        value: getUniqueValues(hubValue),
      });
      delete payload.hub;
      delete payload.hubName;
    } else {
      payload.attributes.push({
        key: payload.hub.fieldName,
        value: [],
      });
      delete payload.hub;
      delete payload.hubName;
    }
  }
  if (payload.deliveryArea) {
    let attrValue = [];
    if (payload?.deliveryArea?.value?.length) {
      attrValue = getFilterAttributeValue(payload.deliveryArea.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryArea.fieldName,
      value: attrValue,
    });
    delete payload.deliveryArea;
  }

  if (payload.deliveryZone) {
    let attrValue = [];
    if (payload?.deliveryZone?.value?.length) {
      attrValue = getFilterAttributeValue(payload.deliveryZone.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryZone.fieldName,
      value: attrValue,
    });
    delete payload.deliveryZone;
  }

  if (payload.orderType) {
    let attrValue = [];
    if (payload.orderType.value) {
      attrValue = getFilterAttributeValue(payload.orderType.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.orderType.fieldName,
      value: attrValue,
    });
    delete payload.orderType;
  }
  if (payload.deliveryStatus) {
    let attrValue = [];
    if (payload?.deliveryStatus?.value.length) {
      attrValue = getFilterAttributeStatus(
        payload.deliveryStatus.value,
        AppConstants.CUSTOMER_COMM_CONSTANTSـSTD.CONSIGNMENTS_DATA
          .NON_TERMINAL_STATUSES
      );
    } else {
      attrValue =
        AppConstants.CUSTOMER_COMM_CONSTANTSـSTD.CONSIGNMENTS_DATA
          .NON_TERMINAL_STATUSES;
    }
    payload.attributes.push({
      key: payload.deliveryStatus.fieldName,
      value: attrValue,
    });
    delete payload.deliveryStatus;
  }

  if (isExpress) {
    if (payload.deliveryProposition) {
      payload.attributes.push({
        key: payload.deliveryProposition.fieldName,
        value: ["EXP"],
      });
      delete payload.deliveryProposition;
    }
  } else {
    if (payload.deliveryProposition) {
      /* let attrValue = [];
      if (payload.deliveryProposition.value) {
        attrValue = getFilterAttributeValue(payload.deliveryProposition.value);
      } else {
        attrValue = [];
      } */
      payload.attributes.push({
        key: payload.deliveryProposition.fieldName,
        value: ["STD"],
      });
      delete payload.deliveryProposition;
    }
  }

  if (payload.dateRange && payload.dateRange.value) {
    const dateRange = [];
    payload.isDateRange = true;
    dateRange.push({
      startTime: moment(
        payload.dateRange.value.startDate,
        AppConstants.DATE_FORMAT
      )
        .hours(0)
        .minutes(0)
        .seconds(0)
        .format(AppConstants.DATE_FORMAT_BACKEND),
      endTime: moment(payload.dateRange.value.endDate, AppConstants.DATE_FORMAT)
        .hours(23)
        .minutes(59)
        .seconds(59)
        .format(AppConstants.DATE_FORMAT_BACKEND),
    });
    payload.dateRange = dateRange;
  }

  if (payload.deliveryEndTime || payload.deliveryStartTime) {
    if (payload.dateRange) {
      payload.isDateRange = true;
      const dateRange: any[] = [];
      if (payload.deliveryEndTime && !payload.deliveryStartTime) {
        const [hours, mins] = payload.deliveryEndTime.split(":");
        dateRange.push({
          startTime: moment(payload.dateRange.startTime)
            .hours(0)
            .minutes(0)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(payload.dateRange.endTime)
            .hours(hours)
            .minutes(mins)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        });
      } else if (payload.deliveryStartTime && !payload.deliveryEndTime) {
        const [hours, mins] = payload.deliveryStartTime.split(":");
        dateRange.push({
          startTime: moment(payload.dateRange.startTime)
            .hours(hours)
            .minutes(mins)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(payload.dateRange.endTime)
            .hours(23)
            .minutes(59)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        });
      } else {
        const [hoursST, minsST] = payload.deliveryStartTime.split(":");

        const [hours, mins] = payload.deliveryEndTime.split(":");
        dateRange.push({
          startTime: moment(payload.dateRange.startTime)
            .hours(hoursST)
            .minutes(minsST)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(payload.dateRange.endTime)
            .hours(hours)
            .minutes(mins)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        });
      }

      payload.dateRange = dateRange;
    }
  }

  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }
  if (data.continuationToken && payload.pageNumber) {
    payload.requestContinuationToken = data.continuationToken;
  }
  if (isExpress) {
    payload.isExpress = true;
  }
  delete payload.deliverySlot;
  delete payload.addressStatus;
  delete payload.deliveryEndTime;
  delete payload.deliveryStartTime;

  return payload;
};

export const createFilterPayloadExpress = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  countryCode?: any,
  selectedChip?: any
) => {
  payload.attributes = [];
  if (countryCode) {
    payload.countryISOCode = countryCode;
  }
  if (payload.dateRange) {
    if (payload.dateRange && payload.dateRange.value) {
      payload.isDateRange = true;
      payload.dateRange = {
        startTime: moment(
          payload.dateRange.value.startDate,
          AppConstants.DATE_FORMAT
        )
          .hours(0)
          .minutes(0)
          .seconds(0)
          .format(AppConstants.DATE_FORMAT_BACKEND),
        endTime: moment(
          payload.dateRange.value.endDate,
          AppConstants.DATE_FORMAT
        )
          .hours(23)
          .minutes(59)
          .seconds(59)
          .format(AppConstants.DATE_FORMAT_BACKEND),
      };
    }
  }
  if (payload.deliveryEndTime || payload.deliveryStartTime) {
    if (payload.dateRange) {
      payload.isDateRange = true;
      let dateRange: any = {};
      if (payload.deliveryEndTime && !payload.deliveryStartTime) {
        const [hours, mins] = payload.deliveryEndTime.split(":");
        dateRange = {
          startTime: moment(payload.dateRange.startTime)
            .hours(0)
            .minutes(0)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(payload.dateRange.endTime)
            .hours(hours)
            .minutes(mins)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        };
      } else if (payload.deliveryStartTime && !payload.deliveryEndTime) {
        const [hours, mins] = payload.deliveryStartTime.split(":");
        dateRange = {
          startTime: moment(payload.dateRange.startTime)
            .hours(hours)
            .minutes(mins)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(payload.dateRange.endTime)
            .hours(23)
            .minutes(59)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        };
      } else {
        const [hoursST, minsST] = payload.deliveryStartTime.split(":");

        const [hours, mins] = payload.deliveryEndTime.split(":");
        dateRange = {
          startTime: moment(payload.dateRange.startTime)
            .hours(hoursST)
            .minutes(minsST)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(payload.dateRange.endTime)
            .hours(hours)
            .minutes(mins)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        };
      }

      payload.dateRange = dateRange;
    }
  }
  if (payload.hub.value.length > 0) {
    let hubValue = payload.hub.value.map((item: any) => item.value);
    let hubNameValue = [];
    if (payload.hubName && payload.hubName.value.length) {
      hubNameValue = payload.hubName.value.map((item: any) => item.value);
    }
    hubValue = hubValue.concat(hubNameValue);
    const selectAllIdx = hubValue.indexOf(AppConstants.SELECT_ALL.value);
    const selectNoneIdx = hubValue.indexOf(AppConstants.SELECT_NONE.value);
    if (selectAllIdx > -1) {
      hubValue = [];
    }
    if (selectNoneIdx > -1) {
      hubValue = [];
    }
    if (hubValue.length) {
      payload.attributes.push({
        key: "hub",
        value: getUniqueValues(hubValue),
      });
    }
    delete payload.hub;
  } else {
    delete payload.hub;
  }
  if (payload.hubName.value.length > 0) {
    let attrValue = [];
    if (payload.hubName.value) {
      attrValue = getFilterAttributeValue(payload.hubName.value);
      payload.attributes.push({
        key: "hubNames",
        value: attrValue,
      });
    }
  } else {
    delete payload.hubName;
  }
  if (payload.deliveryArea.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryArea.value) {
      attrValue = getFilterAttributeValue(payload.deliveryArea.value);
      payload.attributes.push({
        key: payload.deliveryArea.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryArea;
  }
  if (payload.addressStatus.value.length > 0) {
    let attrValue = [];
    if (payload.addressStatus.value) {
      attrValue = getFilterAttributeValue(payload.addressStatus.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.addressStatus.key,
      value: attrValue,
    });
  } else {
    delete payload.addressStatus;
  }
  if (payload.deliveryZone.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryZone.value) {
      attrValue = getFilterAttributeValue(payload.deliveryZone.value);
      payload.attributes.push({
        key: payload.deliveryZone.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryZone;
  }
  if (payload.deliveryStatus.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryStatus.value) {
      attrValue = getFilterAttributeValue(payload.deliveryStatus.value);
      payload.attributes.push({
        key: payload.deliveryStatus.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryStatus;
  }
  if (payload.deliveryExecutionStatus.value.length > 0) {
    let attrValue = [];
    if (payload.deliveryExecutionStatus.value) {
      attrValue = getFilterAttributeValue(
        payload.deliveryExecutionStatus.value
      );
      payload.attributes.push({
        key: payload.deliveryExecutionStatus.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliveryExecutionStatus;
  }
  if (payload.status.value.length > 0) {
    let attrValue = [];
    if (payload.status.value) {
      attrValue = getFilterAttributeStatus(
        payload.status.value,
        AppConstants.CUSTOMER_COMM_CONSTANTS.CONSIGNMENTS_DATA
          .NON_TERMINAL_STATUSES
      );
      payload.attributes.push({
        key: payload.status.key,
        value: attrValue,
      });
    }
  } else {
    payload.attributes.push({
      key: payload.status.key,
      value:
        AppConstants.CUSTOMER_COMM_CONSTANTS.CONSIGNMENTS_DATA
          .NON_TERMINAL_STATUSES,
    });
  }
  if (payload.driverId.value.length > 0) {
    let attrValue = [];
    if (payload.driverId.value) {
      attrValue = getFilterAttributeValue(payload.driverId.value);
      payload.attributes.push({
        key: payload.driverId.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.driverId;
  }
  if (payload.deliverySlotTime.value.length > 0) {
    let attrValue = [];
    if (payload.deliverySlotTime.value) {
      attrValue = getFilterAttributeValue(payload.deliverySlotTime.value);
      payload.attributes.push({
        key: payload.deliverySlotTime.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.deliverySlotTime;
  }
  if (payload.paymentType.value.length > 0) {
    let attrValue = [];
    if (payload.paymentType.value) {
      attrValue = getFilterAttributeValue(payload.paymentType.value);
      payload.attributes.push({
        key: payload.paymentType.key,
        value: attrValue,
      });
    }
  } else {
    delete payload.paymentType;
  }

  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }
  payload.isDateRange = true;
  delete payload.hub;
  delete payload.hubName;
  delete payload.paymentType;
  delete payload.vehicle;
  delete payload.addressStatus;
  delete payload.reset;
  delete payload.deliveryArea;
  delete payload.deliveryStatus;
  delete payload.deliveryExecutionStatus;
  delete payload.status;
  delete payload.deliverySlotTime;
  delete payload.driverId;
  delete payload.deliveryZone;
  delete payload.deliveryEndTime;
  delete payload.deliveryStartTime;
  return payload;
};
